import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import "./login.css";

const highlights = [
  {
    title: "Payroll",
    text: "Run, review, and approve payroll with control.",
  },
  {
    title: "Variations",
    text: "Process staff changes with traceability.",
  },
  {
    title: "Compliance",
    text: "Generate tax and statutory reports with ease.",
  },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    identifier: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from?.pathname || "/dashboard";

  const welcome = useMemo(
    () => ({
      badge: "Enterprise Payroll Suite",
      title: "Enterprise payroll, simplified.",
      subtitle:
        "Securely manage payroll operations, staff variations, and compliance from one central workspace.",
    }),
    []
  );

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(form);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="payroll-auth">
      <div className="payroll-auth__glow payroll-auth__glow--one" />
      <div className="payroll-auth__glow payroll-auth__glow--two" />

      <div className="payroll-auth__shell">
        <section className="payroll-auth__hero">
          <div className="payroll-auth__badge">
            <span className="payroll-auth__badge-dot">EP</span>
            <span>{welcome.badge}</span>
          </div>

          <h1>{welcome.title}</h1>
          <p>{welcome.subtitle}</p>

          <div className="payroll-auth__cards">
            {highlights.map((item) => (
              <div key={item.title} className="payroll-auth__card">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="payroll-auth__panel">
          <div className="payroll-auth__panel-inner">
            <h2>Sign in</h2>
            <p>Access your payroll workspace.</p>

            <form onSubmit={handleSubmit} className="payroll-auth__form">
              {error ? <div className="payroll-auth__error">{error}</div> : null}

              <div className="payroll-auth__field">
                <label htmlFor="identifier">Email or Username</label>
                <input
                  id="identifier"
                  type="text"
                  value={form.identifier}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      identifier: e.target.value,
                    }))
                  }
                  placeholder="Enter your email or username"
                  autoComplete="username"
                />
              </div>

              <div className="payroll-auth__field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>

              <div className="payroll-auth__meta">
                <button type="button" className="payroll-auth__link-button">
                  Forgot password?
                </button>
              </div>

              <button type="submit" disabled={loading} className="payroll-auth__submit">
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="payroll-auth__footer">Secure payroll platform</div>
          </div>
        </section>
      </div>
    </div>
  );
}
